import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import AnimatedModalShell from '../common/AnimatedModalShell';
import AutoHeight from '../common/AutoHeight';
import MfaStepperHeader from './mfa/MfaStepperHeader';
import { mfaModalPanelStyle, mfaHeaderWrapStyle, mfaStepSubStyle } from './mfa/mfaStyles';
import useOverlayFooter from '../common/overlay/useOverlayFooter';
import { foundation } from '../../ui/tokens';
import { modalBodyScrollRegion } from '../../ui/overlayStyles';
import { overlayLayers } from '../../ui/overlayLayers';
import { useModalKeyboard } from '../../hooks/ui/useModalKeyboard';
import { useResponsiveLayout } from '../../hooks/ui/useResponsiveLayout';
import useReducedMotion from '../../hooks/ai/useReducedMotion';
import { getIdleWarningLeadMs } from '../../config/sessionPolicy';

const f = foundation;
const fc = f.color;
const RING = 52;
const R = 23.5;
const CIRC = 2 * Math.PI * R;
/** Above SplashOverlay (100000): the sign-out splash is already rising when the ending shows. */
const ABOVE_SIGN_OUT_SPLASH_Z = 100001;

/** m:ss for a millisecond count, rounded UP so "0:00" only shows at the real sign-out. */
export function formatCountdown(ms) {
  const total = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/**
 * Idle-timeout warning (KAN-608, RD-UI-021): "Still there?" with a LIVE
 * countdown ring, ONE blue "Stay signed in" button and a quiet "Sign out now"
 * link. The countdown reads `getRemainingMs` — the idle hook's own
 * lastActivity + timeout, the same clock its 1s warning tick signs out on —
 * never a separate timer. ✕ / Escape / scrim all mean "stay" (the safe
 * choice) and play the exit beat; "Sign out now" hands straight to the
 * caller's sign-out (the app tears the dialog down itself).
 */
export default function SessionTimeoutWarning({
  onStay, onSignOutNow, getRemainingMs, getLastActivityAt, expired = false, onExpiredDone,
}) {
  const intl = useIntl();
  const reduced = useReducedMotion();
  const { isMobile, isTablet } = useResponsiveLayout();
  const [compact] = useState(() => isMobile || isTablet); // layout-by-width: the panel's layout, latched; button sizes come from the footer
  // The ONE footer (KAN-948): 38px web / 44px touch, the quiet choice outlined on the left, "Stay" on the right.
  const footer = useOverlayFooter();
  const [animOpen, setAnimOpen] = useState(true);
  const read = useCallback(() => {
    const v = getRemainingMs?.();
    return v == null ? getIdleWarningLeadMs() : v;
  }, [getRemainingMs]);
  const [remaining, setRemaining] = useState(read);
  // The idle check skips while offline, so a countdown would freeze at a lie.
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine !== false));
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);
  const stayingRef = useRef(false);
  // Real user activity after the warning opened = the idle clock restarted.
  const shownAtActivityRef = useRef(getLastActivityAt?.());

  const expiredRef = useRef(expired);
  expiredRef.current = expired;
  const requestStay = useCallback(() => {
    if (stayingRef.current || expiredRef.current) return; // too late to stay: the sign-out has started
    stayingRef.current = true;
    setAnimOpen(false);
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      if (expiredRef.current) return;
      const ms = read();
      setRemaining(ms);
      // Driven by the hook's REAL last-activity stamp (a DOM event), never by
      // inferred remaining time — a clock reset by anything else must not hide
      // the warning.
      const at = getLastActivityAt?.();
      if (at != null && shownAtActivityRef.current != null && at > shownAtActivityRef.current) requestStay();
    }, 1000);
    return () => clearInterval(id);
  }, [read, requestStay, getLastActivityAt]);

  // Expiry: the real sign-out already started at 0:00; the pop-up simply plays its
  // normal exit beat (above the rising sign-out splash), then onExitComplete hands
  // off to onExpiredDone. The login screen's inactivity notice is the message.
  useEffect(() => { if (expired) setAnimOpen(false); }, [expired]);
  const onExitComplete = useCallback(() => {
    if (expiredRef.current) onExpiredDone?.(); else onStay?.();
  }, [onExpiredDone, onStay]);

  // Continuous drain: the arc is written straight to the DOM every frame (no React
  // state per frame); the number re-renders once per whole second, in the same frame.
  const arcRef = useRef(null);
  useEffect(() => {
    if (reduced || expired || typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') return undefined;
    let raf = 0;
    let lastSec = Math.ceil(read() / 1000);
    const lead = getIdleWarningLeadMs();
    const tick = () => {
      const ms = read();
      const p = Math.min(1, Math.max(0, ms / lead));
      arcRef.current?.setAttribute('stroke-dashoffset', String(CIRC * (1 - p)));
      const sec = Math.ceil(ms / 1000);
      if (sec !== lastSec) { lastSec = sec; setRemaining(ms); }
      if (ms > 0) raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame?.(raf);
  }, [reduced, expired, read]);

  useModalKeyboard(animOpen && !expired, { onEscape: requestStay, onEnter: () => requestStay() });

  const title = intl.formatMessage({ id: 'auth.sessionWarning.title', defaultMessage: 'Still there?' });
  const clock = formatCountdown(remaining);
  const progress = reduced ? 1 : Math.min(1, Math.max(0, remaining / getIdleWarningLeadMs()));
  const sentence = online
    ? intl.formatMessage({ id: 'auth.sessionWarning.message', defaultMessage: "For your security, we'll sign you out for inactivity." })
    : intl.formatMessage({ id: 'auth.notices.connectionLost', defaultMessage: 'Connection lost. Reconnecting…' });
  const timerLabel = online
    ? intl.formatMessage({ id: 'auth.sessionWarning.countdown', defaultMessage: 'Signing out in {time}' }, { time: clock })
    : sentence;
  const warnBody = (
    <div style={{ display: 'flex', alignItems: 'center', gap: f.spacePx[3] }}>
      <span
        data-testid="session-warning-ring"
        style={{ position: 'relative', width: RING, height: RING, flexShrink: 0, display: 'inline-flex' }}
      >
        <svg width={RING} height={RING} viewBox={`0 0 ${RING} ${RING}`} aria-hidden>
          <circle cx="26" cy="26" r={R} fill="none" stroke={fc.neutral.border} strokeWidth="5" />
          <circle
            ref={arcRef}
            data-testid="session-warning-arc"
            cx="26" cy="26" r={R} fill="none" stroke={fc.brand.primary} strokeWidth="5" strokeLinecap="round"
            strokeDasharray={CIRC} strokeDashoffset={CIRC * (1 - progress)} transform="rotate(-90 26 26)"
          />
        </svg>
        <span
          role="timer"
          aria-live="off"
          aria-label={timerLabel}
          data-testid="session-warning-countdown"
          style={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: fc.neutral.text,
            fontFamily: f.fontFamily.sans,
          }}
        >
          {online ? clock : '–'}
        </span>
      </span>
      <p style={{ ...mfaStepSubStyle, margin: 0, flex: 1, minWidth: 0 }}>{sentence}</p>
    </div>
  );

  return (
    <AnimatedModalShell
      isOpen={animOpen}
      onExitComplete={onExitComplete}
      onScrimClick={requestStay}
      zIndex={expired ? ABOVE_SIGN_OUT_SPLASH_Z : overlayLayers.adminModal}
      dialogStyle={mfaModalPanelStyle(compact, 440)}
      dialogProps={{ role: 'alertdialog', 'aria-modal': 'true', 'aria-label': title }}
    >
      <div style={mfaHeaderWrapStyle}>
        <MfaStepperHeader variant="titled" title={title} canClose={!expired} onClose={requestStay} />
      </div>
      <div style={modalBodyScrollRegion}>
        <AutoHeight>{warnBody}</AutoHeight>
      </div>
      <div style={footer.row}>
        <button type="button" data-testid="session-warning-signout" style={footer.cancel} onClick={expired ? undefined : onSignOutNow}>
          {intl.formatMessage({ id: 'auth.sessionWarning.cancel', defaultMessage: 'Sign out now' })}
        </button>
        <button type="button" data-testid="session-warning-stay" style={footer.primary} onClick={requestStay}>
          {intl.formatMessage({ id: 'auth.sessionWarning.confirm', defaultMessage: 'Stay signed in' })}
        </button>
      </div>
    </AnimatedModalShell>
  );
}
